import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import * as authApi from '@/api/endpoints/auth'
import { onAuthEvent } from '@/api/client'
import { ApiError } from '@/api/errors'
import type { SessionResponseParsed } from '@/api/schemas'

/** Who is signed in, and what they may see. */

export interface AuthState {
  status: 'loading' | 'authenticated' | 'anonymous'
  user: SessionResponseParsed['user'] | null
  permissions: ReadonlySet<string>
  session: SessionResponseParsed['session'] | null
  /** True while the forced first-sign-in change is outstanding ( */
  mustChangePassword: boolean
}

export interface AuthContextValue extends AuthState {
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  changePassword: (current: string, next: string) => Promise<void>
  /** Re-read `/auth/me`. How a revoked session or an edited role reaches an open tab. */
  refresh: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

const ANONYMOUS: AuthState = {
  status: 'anonymous',
  user: null,
  permissions: new Set(),
  session: null,
  mustChangePassword: false,
}

function toState(res: SessionResponseParsed): AuthState {
  return {
    status: 'authenticated',
    user: res.user,
    permissions: new Set(res.permissions),
    session: res.session,
    mustChangePassword: res.user.must_change_password,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ ...ANONYMOUS, status: 'loading' })
  const queryClient = useQueryClient()

  // Guards the refocus revalidation: two tabs regaining focus together.
  const revalidating = useRef(false)

  /** Drop every cached query on sign-out and on sign-in. */
  const clearCache = useCallback(() => {
    queryClient.clear()
  }, [queryClient])

  const refresh = useCallback(async () => {
    try {
      setState(toState(await authApi.me()))
    } catch (error) {
      // A 401 here is the ordinary "not signed in" answer on first boot, not.
      if (error instanceof ApiError || error instanceof Error) setState(ANONYMOUS)
      else setState(ANONYMOUS)
    }
  }, [])

  // Boot.
  useEffect(() => {
    void refresh()
  }, [refresh])

  /** Revalidate when the window regains focus. */
  useEffect(() => {
    function onFocus(): void {
      if (document.visibilityState !== 'visible') return
      if (revalidating.current) return
      revalidating.current = true
      void refresh().finally(() => {
        revalidating.current = false
      })
    }
    window.addEventListener('visibilitychange', onFocus)
    window.addEventListener('focus', onFocus)
    return () => {
      window.removeEventListener('visibilitychange', onFocus)
      window.removeEventListener('focus', onFocus)
    }
  }, [refresh])

  /** Any 401 anywhere in the app lands here. */
  useEffect(
    () =>
      onAuthEvent((event) => {
        if (event === 'expired') {
          setState(ANONYMOUS)
          clearCache()
        } else {
          void refresh()
        }
      }),
    [clearCache, refresh],
  )

  const signIn = useCallback(
    async (email: string, password: string) => {
      // Clear first: a previous session's cached data must not survive into.
      clearCache()
      setState(toState(await authApi.login(email, password)))
    },
    [clearCache],
  )

  const signOut = useCallback(async () => {
    try {
      await authApi.logout()
    } finally {
      setState(ANONYMOUS)
      clearCache()
    }
  }, [clearCache])

  const changePassword = useCallback(
    async (current: string, next: string) => {
      await authApi.changePassword(current, next)
      // Re-read rather than patching the flag locally: the server also revoked
      await refresh()
    },
    [refresh],
  )

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signIn, signOut, changePassword, refresh }),
    [state, signIn, signOut, changePassword, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
